import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Fab,
  Stack,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Folder as FolderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
  AttachMoney as AttachMoneyIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Router as RouterIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Planos, PlanosGrupo } from '../types';
import { planosService } from '../services/planosService';
import { planosGruposService } from '../services/planosGruposService';
import PlanosForm from '../components/forms/PlanosForm';
import PlanosGruposModal from '../components/modals/PlanosGruposModal';
import PlanosServicosModal from '../components/modals/PlanosServicosModal';

const CORES_GRUPO: Record<string, { cor: string; bg: string }> = {
  Residencial: { cor: '#1565c0', bg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' },
  Empresarial: { cor: '#6a1b9a', bg: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)' },
  Rural: { cor: '#2e7d32', bg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' },
};

const getGrupoStyle = (titulo?: string) => {
  if (titulo && CORES_GRUPO[titulo]) return CORES_GRUPO[titulo];
  return { cor: '#0277bd', bg: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)' };
};

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 2,
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    }}
  >
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
            {titulo}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>
            {valor}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
          {icone}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

interface PlanoCardProps {
  plano: Planos;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, plano: Planos) => void;
  formatPrice: (price: number) => string;
  onServicosClick: (plano: Planos, filtro: 'ativos' | 'cancelados') => void;
}

const PlanoCard: React.FC<PlanoCardProps> = ({ plano, onMenuOpen, formatPrice, onServicosClick }) => {
  const theme = useTheme();
  const grupoTitulo = plano.grupo?.titulo;
  const grupoStyle = getGrupoStyle(grupoTitulo);
  const recursos = Array.isArray(plano.recursos) ? plano.recursos : [];
  const escritorios = plano.escritorios ?? [];
  const qtdeAtivos = plano.servicos_nao_cancelados ?? 0;
  const qtdeCancelados = plano.servicos_cancelados ?? 0;
  const escritorioLabel =
    escritorios.length > 0
      ? `${escritorios.length} escritório${escritorios.length !== 1 ? 's' : ''}`
      : plano.escritorio_id?.length > 0
        ? `${plano.escritorio_id.length} escritório(s)`
        : null;
  const escritorioFallback = !escritorios.length && (plano.escritorio_id?.length ?? 0) > 0;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        opacity: plano.ativo ? 1 : 0.75,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          opacity: 1,
          borderColor: alpha(grupoStyle.cor, 0.45),
          boxShadow: `0 4px 20px ${alpha(grupoStyle.cor, 0.12)}`,
          transform: 'translateY(-2px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: plano.ativo ? grupoStyle.cor : alpha(theme.palette.text.disabled, 0.5),
        },
      }}
    >
      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  background: plano.ativo ? grupoStyle.bg : alpha(theme.palette.action.disabledBackground, 0.5),
                  border: `1.5px solid ${alpha(grupoStyle.cor, plano.ativo ? 0.4 : 0.2)}`,
                }}
              >
                <SpeedIcon sx={{ fontSize: 16, color: plano.ativo ? grupoStyle.cor : 'text.disabled' }} />
              </Avatar>
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={800} noWrap title={plano.nome}>
                  {plano.nome}
                </Typography>
                {grupoTitulo && (
                  <Chip
                    label={grupoTitulo}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      mt: 0.25,
                      bgcolor: alpha(grupoStyle.cor, 0.12),
                      color: grupoStyle.cor,
                      border: `1px solid ${alpha(grupoStyle.cor, 0.25)}`,
                    }}
                  />
                )}
              </Box>
            </Stack>
          </Box>
          <IconButton size="small" onClick={(e) => onMenuOpen(e, plano)} sx={{ ml: 0.5, mt: -0.25 }}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Velocidades + Preço */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            mb: plano.descricao || recursos.length ? 0.75 : 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', minWidth: 0 }}>
          <CloudDownloadIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.8, flexShrink: 0, mr: 0.5 }} />
          <Box component="span" fontWeight={700} color="text.primary">
              {plano.velocidade_download} 
            </Box>
            
            {' Download | '}
            <CloudUploadIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.8, flexShrink: 0, mr: 0.5 }} />
              <Box component="span" fontWeight={700} color="text.primary">
              {plano.velocidade_upload}
            </Box>
            {' Upload'}
            
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0 }}>
            <Typography variant="h6" fontWeight={800} color={plano.ativo ? 'primary.main' : 'text.secondary'} lineHeight={1}>
              {formatPrice(plano.valor)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              /mês
            </Typography>
          </Box>
        </Box>

        {plano.descricao && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 0.75,
              lineHeight: 1.35,
            }}
          >
            {plano.descricao}
          </Typography>
        )}

        {recursos.length > 0 && (
          <Box mb={0.75}>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {recursos.slice(0, 2).map((recurso, index) => (
                <Chip
                  key={index}
                  label={recurso}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.62rem', fontWeight: 500 }}
                />
              ))}
              {recursos.length > 2 && (
                <Chip label={`+${recursos.length - 2}`} size="small" sx={{ height: 20, fontSize: '0.62rem' }} />
              )}
            </Stack>
          </Box>
        )}

        {/* Serviços vinculados */}
        <Paper
          elevation={0}
          sx={{
            p: 0.75,
            mb: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.4 : 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
            <PeopleIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.85 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', fontWeight: 600 }}>
              Serviços vinculados
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <Box
              onClick={() => onServicosClick(plano, 'ativos')}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                px: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.success.main, qtdeAtivos > 0 ? 0.1 : 0.04),
                border: `1px solid ${alpha(theme.palette.success.main, qtdeAtivos > 0 ? 0.3 : 0.12)}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.success.main, 0.16),
                  borderColor: alpha(theme.palette.success.main, 0.45),
                },
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
                Ativos
              </Typography>
              <Typography variant="body2" fontWeight={800} color={qtdeAtivos > 0 ? 'success.main' : 'text.secondary'} lineHeight={1.2}>
                {qtdeAtivos}
              </Typography>
            </Box>
            <Box
              onClick={() => onServicosClick(plano, 'cancelados')}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                px: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.error.main, qtdeCancelados > 0 ? 0.08 : 0.04),
                border: `1px solid ${alpha(theme.palette.error.main, qtdeCancelados > 0 ? 0.25 : 0.1)}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.14),
                  borderColor: alpha(theme.palette.error.main, 0.4),
                },
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
                Cancelados
              </Typography>
              <Typography variant="body2" fontWeight={800} color={qtdeCancelados > 0 ? 'error.main' : 'text.secondary'} lineHeight={1.2}>
                {qtdeCancelados}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Chip
            icon={plano.ativo ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
            label={plano.ativo ? 'Ativo' : 'Inativo'}
            size="small"
            color={plano.ativo ? 'success' : 'default'}
            variant={plano.ativo ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
          />
          {escritorioLabel && (
            <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0} sx={{ maxWidth: '55%' }}>
              {escritorioFallback ? (
                <RouterIcon sx={{ fontSize: 13, color: 'warning.main', opacity: 0.8, flexShrink: 0 }} />
              ) : (
                <BusinessIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.8, flexShrink: 0 }} />
              )}
              <Typography
                variant="caption"
                color={escritorioFallback ? 'warning.main' : 'text.secondary'}
                noWrap
                title={escritorioLabel}
              >
                {escritorioLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const PlansPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [planos, setPlanos] = useState<Planos[]>([]);
  const [grupos, setGrupos] = useState<PlanosGrupo[]>([]);
  const [filtroGrupoId, setFiltroGrupoId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState<Planos | null>(null);
  const [gruposModalOpen, setGruposModalOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPlano, setMenuPlano] = useState<Planos | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planoToDelete, setPlanoToDelete] = useState<Planos | null>(null);

  const [servicosModalOpen, setServicosModalOpen] = useState(false);
  const [servicosModalPlano, setServicosModalPlano] = useState<Planos | null>(null);
  const [servicosModalFiltro, setServicosModalFiltro] = useState<'ativos' | 'cancelados'>('ativos');

  const loadPlans = async (silent = false) => {
    try {
      if (!silent) setError(null);
      const response = await planosService.getAll();
      setPlanos(response.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar planos:', err);
      if (!silent) setError('Erro ao carregar planos');
    }
  };

  const loadGrupos = async () => {
    try {
      const response = await planosGruposService.getAll();
      if (response.data) {
        setGrupos(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
    }
  };

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      try {
        await Promise.all([loadPlans(), loadGrupos()]);
      } catch (error) {
        console.error('Erro ao inicializar página:', error);
      } finally {
        setLoading(false);
      }
    };
    initializePage();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadPlans(true), loadGrupos()]);
    setRefreshing(false);
  };

  const loadPlansOnly = () => loadPlans(true);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, plano: Planos) => {
    setAnchorEl(event.currentTarget);
    setMenuPlano(plano);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlano(null);
  };

  const handleCreatePlano = () => {
    setSelectedPlano(null);
    setFormOpen(true);
  };

  const handleEditPlano = (plano: Planos) => {
    setSelectedPlano(plano);
    setFormOpen(true);
    handleMenuClose();
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedPlano(null);
  };

  const handleFormSuccess = () => {
    loadPlansOnly();
    loadGrupos();
  };

  const handleDeleteClick = (plano: Planos) => {
    setPlanoToDelete(plano);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!planoToDelete) return;
    try {
      await planosService.delete(planoToDelete.id);
      setDeleteDialogOpen(false);
      setPlanoToDelete(null);
      loadPlansOnly();
      loadGrupos();
    } catch (err: any) {
      console.error('Erro ao excluir plano:', err);
      const apiMsg = err?.response?.data?.message || err?.response?.data?.errors?.[0];
      setError(apiMsg || 'Erro ao excluir plano');
    }
  };

  const handleToggleStatus = async (plano: Planos) => {
    try {
      const response = await fetch(`/api/planos/${plano.id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        loadPlansOnly();
      } else {
        setError('Erro ao alterar status do plano');
      }
    } catch (err: any) {
      console.error('Erro ao alterar status:', err);
      setError('Erro ao alterar status do plano');
    }
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPlanoToDelete(null);
  };

  const handleServicosClick = (plano: Planos, filtro: 'ativos' | 'cancelados') => {
    setServicosModalPlano(plano);
    setServicosModalFiltro(filtro);
    setServicosModalOpen(true);
  };

  const handleServicosModalClose = () => {
    setServicosModalOpen(false);
    setServicosModalPlano(null);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  const planosFiltrados = useMemo(() => {
    if (!filtroGrupoId) return planos;
    return planos.filter((plano) => plano.grupo_id === filtroGrupoId);
  }, [planos, filtroGrupoId]);

  const activePlans = useMemo(() => planosFiltrados.filter((p) => p.ativo), [planosFiltrados]);
  const inactivePlans = useMemo(() => planosFiltrados.filter((p) => !p.ativo), [planosFiltrados]);

  const resumo = useMemo(() => {
    const ativos = planos.filter((p) => p.ativo).length;
    const inativos = planos.filter((p) => !p.ativo).length;
    const mediaValor = planos.length
      ? planos.reduce((acc, p) => acc + Number(p.valor), 0) / planos.length
      : 0;
    return { ativos, inativos, mediaValor, totalGrupos: grupos.length };
  }, [planos, grupos]);

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${alpha(theme.palette.info.dark, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#e3f2fd', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  const renderSection = useCallback(
    (titulo: string, lista: Planos[], icone: React.ReactNode, chipColor: 'success' | 'default') => {
      if (lista.length === 0) return null;
      return (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              {icone}
              <Typography variant="body2" fontWeight={700}>
                {titulo}
              </Typography>
            </Stack>
            <Chip
              size="small"
              label={`${lista.length} plano${lista.length !== 1 ? 's' : ''}`}
              color={chipColor}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Box>
          <Box sx={{ p: 1.25 }}>
            <Grid container spacing={1.25}>
              {lista.map((plan) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={plan.id}>
                  <PlanoCard
                    plano={plan}
                    onMenuOpen={handleMenuOpen}
                    formatPrice={formatPrice}
                    onServicosClick={handleServicosClick}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      );
    },
    [theme]
  );

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -10,
            opacity: 0.08,
            transform: 'rotate(15deg)',
          }}
        >
          <SpeedIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.25}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              <SpeedIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                Gestão de Planos
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gerencie os planos de internet oferecidos
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FolderIcon />}
              onClick={() => setGruposModalOpen(true)}
              fullWidth={isMobile}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                borderColor: alpha(theme.palette.primary.main, 0.3),
                fontWeight: 600,
              }}
            >
              Gerenciar Grupos
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreatePlano}
              fullWidth={isMobile}
              sx={{ fontWeight: 600, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` }}
            >
              Novo Plano
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPIs */}
      {!loading && planos.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Total de Planos"
              valor={planos.length}
              icone={<SpeedIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Planos Ativos"
              valor={resumo.ativos}
              subtitulo={`${planos.length ? Math.round((resumo.ativos / planos.length) * 100) : 0}% do total`}
              icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Grupos"
              valor={resumo.totalGrupos}
              subtitulo={filtroGrupoId ? 'Filtro ativo' : 'Todos visíveis'}
              icone={<GroupsIcon sx={{ color: theme.palette.info.main }} />}
              cor={theme.palette.info.main}
              bgCor={alpha(theme.palette.info.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Ticket Médio"
              valor={formatPrice(resumo.mediaValor)}
              subtitulo={`${resumo.inativos} inativo${resumo.inativos !== 1 ? 's' : ''}`}
              icone={<AttachMoneyIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          </Grid>
        </Grid>
      )}

      {/* Resumo por grupo */}
      {!loading && grupos.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
            <FolderIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            <Typography variant="body2" fontWeight={700}>
              Planos por Grupo
            </Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              label={`Todos (${planos.length})`}
              size="small"
              clickable
              onClick={() => setFiltroGrupoId('')}
              sx={{
                height: 26,
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: filtroGrupoId === '' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                border: `1.5px solid ${alpha(theme.palette.primary.main, filtroGrupoId === '' ? 0.5 : 0.25)}`,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
              }}
            />
            {grupos.map((grupo) => {
              const style = getGrupoStyle(grupo.titulo);
              const selecionado = filtroGrupoId === grupo.id;
              return (
                <Chip
                  key={grupo.id}
                  label={`${grupo.titulo} (${grupo.qtde ?? 0})`}
                  size="small"
                  clickable
                  onClick={() => setFiltroGrupoId(grupo.id)}
                  sx={{
                    height: 26,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor: selecionado ? alpha(style.cor, 0.2) : alpha(style.cor, 0.08),
                    color: style.cor,
                    border: `1.5px solid ${alpha(style.cor, selecionado ? 0.5 : 0.25)}`,
                    '&:hover': { bgcolor: alpha(style.cor, 0.18) },
                  }}
                />
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Conteúdo */}
      {loading ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 5,
            gap: 1,
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="caption" color="text.secondary">
            Carregando planos...
          </Typography>
        </Paper>
      ) : planosFiltrados.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            p: 2.5,
            textAlign: 'center',
          }}
        >
          <SpeedIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {planos.length === 0 ? 'Nenhum plano cadastrado' : 'Nenhum plano neste grupo'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {planos.length === 0
              ? 'Comece criando seu primeiro plano de internet'
              : 'Selecione outro grupo ou cadastre um novo plano'}
          </Typography>
          {planos.length === 0 && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreatePlano}>
              Criar Primeiro Plano
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {renderSection(
            'Planos Ativos',
            activePlans,
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />,
            'success'
          )}
          {renderSection(
            'Planos Inativos',
            inactivePlans,
            <CancelIcon sx={{ color: 'text.secondary', fontSize: 18 }} />,
            'default'
          )}
        </>
      )}

      {/* Menu de Ações */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuPlano && handleEditPlano(menuPlano)}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => menuPlano && handleToggleStatus(menuPlano)}>
          {menuPlano?.ativo ? (
            <>
              <ToggleOffIcon sx={{ mr: 1, fontSize: 18 }} />
              Desativar
            </>
          ) : (
            <>
              <ToggleOnIcon sx={{ mr: 1, fontSize: 18 }} />
              Ativar
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => menuPlano && handleDeleteClick(menuPlano)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Excluir
        </MenuItem>
      </Menu>

      {isMobile && (
        <>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 86, right: 16, zIndex: theme.zIndex.appBar - 1 }}
            onClick={handleCreatePlano}
          >
            <AddIcon />
          </Fab>
          <Fab
            color="secondary"
            aria-label="refresh"
            sx={{ position: 'fixed', bottom: 86, right: 80, zIndex: theme.zIndex.appBar - 1 }}
            onClick={handleRefresh}
          >
            <RefreshIcon />
          </Fab>
        </>
      )}

      <PlanosForm open={formOpen} onClose={handleFormClose} onSuccess={handleFormSuccess} plano={selectedPlano} />

      <PlanosGruposModal
        open={gruposModalOpen}
        onClose={() => setGruposModalOpen(false)}
        onGruposChange={() => {
          loadGrupos();
          loadPlansOnly();
        }}
      />

      <PlanosServicosModal
        open={servicosModalOpen}
        onClose={handleServicosModalClose}
        plano={servicosModalPlano}
        filtro={servicosModalFiltro}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir o plano &quot;{planoToDelete?.nome}&quot;? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlansPage;
